'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';
import { SYD_TOWN_HALL } from '@workspace/constants';
import { ESearchRadiusLevels, ESearchZoomLevels } from '@/app/lib/store-locator-utils';
import { ConfigureProps } from 'react-instantsearch';

export type UsedRadius = number | 'all';
export type LatLng = { lat: number; lng: number };
export type SearchMode = 'radius' | 'bounds';

type GeoRadiusContextValue = {
  usedRadius: UsedRadius;
  setUsedRadius: React.Dispatch<React.SetStateAction<UsedRadius>>;

  center: LatLng;
  setCenter: React.Dispatch<React.SetStateAction<LatLng>>;

  mapZoom: number;
  setMapZoom: React.Dispatch<React.SetStateAction<number>>;

  // Flag to track if viewport bounds have been initialized
  hasViewportBounds: boolean;
  setHasViewportBounds: React.Dispatch<React.SetStateAction<boolean>>;

  // Flag to track if user has interacted with map (drag/zoom)
  hasUserInteracted: boolean;
  setHasUserInteracted: React.Dispatch<React.SetStateAction<boolean>>;

  // Search mode: radius-based or bounds-based
  searchMode: SearchMode;
  setSearchMode: React.Dispatch<React.SetStateAction<SearchMode>>;

  mapBounds: google.maps.LatLngBounds | undefined;
  setMapBounds: React.Dispatch<React.SetStateAction<google.maps.LatLngBounds | undefined>>;

  updateTick: number;
  setUpdateTick: React.Dispatch<React.SetStateAction<number>>;

  setCenterCallback: (latLng: google.maps.LatLngLiteral) => void;

  configureProps: ConfigureProps;
  setConfigureProps: React.Dispatch<React.SetStateAction<ConfigureProps>>;
};

const GeoRadiusContext = createContext<GeoRadiusContextValue | null>(null);

export function GeoRadiusProvider({ children }: { children: React.ReactNode }) {
  const [updateTick, setUpdateTick] = useState<number>(0); // Forcing re-renders if needed
  const [usedRadius, setUsedRadius] = useState<UsedRadius>(ESearchRadiusLevels.LEVEL_1);
  const [center, setCenter] = useState<LatLng>(SYD_TOWN_HALL);
  const [mapZoom, setMapZoom] = useState<number>(ESearchZoomLevels.LEVEL_1);
  const [hasViewportBounds, setHasViewportBounds] = useState<boolean>(false);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const [searchMode, setSearchMode] = useState<SearchMode>('radius');
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | undefined>(undefined);

  const [configureProps, setConfigureProps] = useState<ConfigureProps>({
    aroundLatLng: SYD_TOWN_HALL.lat + ', ' + SYD_TOWN_HALL.lng,
    aroundRadius: ESearchRadiusLevels.LEVEL_1,
    hitsPerPage: 2000,
    insideBoundingBox: undefined,
  });

  const setCenterCallback = useCallback(
    (latLng: google.maps.LatLngLiteral) => {
      setHasUserInteracted(false);
      setCenter(latLng);
      setConfigureProps({
        aroundLatLng: latLng.lat + ', ' + latLng.lng,
        aroundRadius: ESearchRadiusLevels.LEVEL_1,
        hitsPerPage: 2000,
        insideBoundingBox: undefined,
      });
    },
    [setCenter, setHasUserInteracted],
  );

  return (
    <GeoRadiusContext.Provider
      value={{
        usedRadius,
        setUsedRadius,
        center,
        setCenter,
        mapZoom,
        setMapZoom,
        hasViewportBounds,
        setHasViewportBounds,
        hasUserInteracted,
        setHasUserInteracted,
        searchMode,
        setSearchMode,
        mapBounds,
        setMapBounds,
        updateTick,
        setUpdateTick,
        setCenterCallback,
        configureProps,
        setConfigureProps,
      }}
    >
      {children}
    </GeoRadiusContext.Provider>
  );
}

export function useGeoRadius() {
  const ctx = useContext(GeoRadiusContext);
  if (!ctx) throw new Error('useGeoRadius must be used within GeoRadiusProvider');
  return ctx;
}
